/**
 * Ask Nexus — AI-powered natural language query interface.
 * Type a question about your data and get instant answers.
 */

import { useState, useRef, useEffect } from 'react';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sql?: string | null;
  results?: Record<string, unknown>[] | null;
  rowCount?: number;
  error?: string | null;
  explanation?: string;
  timestamp: Date;
}

const EXAMPLE_QUESTIONS = [
  "What's our total spend by vendor this year?",
  "Which branches have no backup circuit (no WAN2)?",
  "How many Meraki devices are offline right now?",
  "Show me the top 10 vendors by invoice count",
  "Which employees are assigned assets at a different site than their location?",
  "What's the total MRC for all circuits by carrier?",
  "List all branches with more than 50 hardware assets",
  "Show me vendors missing a W-9",
];

export function AskNexus() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const askQuestion = async (question: string) => {
    if (!question.trim() || loading) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: question,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });

      const data = await res.json();

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.explanation || '',
        sql: data.sql,
        results: data.results,
        rowCount: data.rowCount,
        error: data.error,
        explanation: data.explanation,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Failed to get a response. Please try again.',
        error: err instanceof Error ? err.message : 'Unknown error',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    askQuestion(input);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Ask Nexus</h1>
          <p className="text-sm text-slate-500">Ask questions about your data in plain English</p>
        </div>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🔍</div>
            <h2 className="text-lg font-medium text-text-primary mb-2">What would you like to know?</h2>
            <p className="text-sm text-slate-500 mb-6">Ask about vendors, invoices, assets, circuits, sites, or anything in Nexus</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl mx-auto">
              {EXAMPLE_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  onClick={() => askQuestion(q)}
                  className="text-left text-sm px-4 py-3 rounded-lg border border-border bg-white hover:border-baker-accent hover:bg-page transition-all cursor-pointer"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-xl px-4 py-3 ${
              msg.role === 'user'
                ? 'bg-baker-red text-white'
                : 'bg-white border border-border'
            }`}>
              {msg.role === 'user' ? (
                <p className="text-sm">{msg.content}</p>
              ) : (
                <div>
                  {msg.error && (
                    <div className="text-sm text-red-600 mb-2 p-2 bg-red-50 rounded">
                      {msg.error}
                    </div>
                  )}

                  {msg.explanation && (
                    <p className="text-sm text-slate-600 mb-2">{msg.explanation}</p>
                  )}

                  {msg.results && msg.results.length > 0 && (
                    <div className="overflow-x-auto mt-2">
                      <div className="text-xs text-slate-400 mb-1">{msg.rowCount} row{msg.rowCount !== 1 ? 's' : ''}</div>
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="border-b border-border">
                            {Object.keys(msg.results[0]).map((col) => (
                              <th key={col} className="text-left py-1.5 px-2 font-medium text-slate-600 whitespace-nowrap">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {msg.results.slice(0, 50).map((row, idx) => (
                            <tr key={idx} className="border-b border-border-subtle hover:bg-page/50">
                              {Object.values(row).map((val, cidx) => (
                                <td key={cidx} className="py-1 px-2 whitespace-nowrap text-slate-700">
                                  {val == null ? '—' : typeof val === 'number'
                                    ? val.toLocaleString(undefined, { maximumFractionDigits: 2 })
                                    : String(val).substring(0, 100)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {msg.results && msg.results.length === 0 && !msg.error && (
                    <p className="text-sm text-slate-400 italic">No results found.</p>
                  )}

                  {msg.sql && (
                    <details className="mt-2">
                      <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">View SQL</summary>
                      <pre className="mt-1 text-xs bg-slate-50 p-2 rounded overflow-x-auto text-slate-600 font-mono">
                        {msg.sql}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-border rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <div className="h-4 w-4 border-2 border-border border-t-baker-accent rounded-full animate-spin" />
                Thinking...
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex gap-3 pt-4 border-t border-border">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question about your data..."
          disabled={loading}
          className="flex-1 px-4 py-3 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all disabled:opacity-50"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="px-6 py-3 rounded-xl bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Ask
        </button>
      </form>
    </div>
  );
}
